// @flow

import type { SplitFactoryConfig } from '@splitsoftware/splitio';
import { env } from './env';

/**
 * Validate that the dynamically imported module has a default export which is an object.
 */
export function isESModule(module: mixed): boolean {
  return typeof module === 'object' && module !== null && 'default' in module;
}

/**
 * Dynamically import local split file as JSON.
 * NOTE: ONLY USE WHEN DEPLOY ENV IS 'local' because we only inject the vite yaml plugin in local.
 */
export function importLocalSplits(): SplitFactoryConfig['features'] | void {
  if (env.LOCAL_SPLITS !== 'true') {
    return undefined;
  }

  const path = './local.split.yaml';

  // $FlowIssue[not-a-function] Flow types `import.meta` as `mixed` and doesn't allow to set our own types for it
  const localSplitYaml = import.meta.glob('./local.split.yaml', {
    eager: true,
  });

  if (!localSplitYaml || !localSplitYaml[path]) {
    throw new Error(
      `Could not find local split file "${path}", did you forget to add it to the project? You can use the "local.split.example.yaml" file as a template.`
    );
  }

  const localSplitYamlModule = localSplitYaml[path];

  // This is a mild validation on the imported module
  if (!isESModule(localSplitYamlModule)) throw new Error(`"${path}" is not an ES module`);

  // Get the `features` field from the imported YAML file.
  // $FlowSuppress[prop-missing]
  const { features } = localSplitYamlModule.default;

  // we expect the `features` field to be a object.
  if (!features || typeof features !== 'object') {
    throw new Error(`Invalid content: ${path}\n${JSON.stringify(module, null, 2)}`);
  }

  return features;
}
